import React, { useContext, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SidebarContext } from './SidebarContext';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Z } from '../../lib/zIndex';

interface SidebarLayoutProps {
  sidebarContent: React.ReactNode;
  mainContent: React.ReactNode;
}

export function SidebarLayout({ sidebarContent, mainContent }: SidebarLayoutProps) {
  const { isOpen, toggle, setHasSidebar } = useContext(SidebarContext);
  const { t } = useTranslation();

  useEffect(() => {
    setHasSidebar(true);
    return () => setHasSidebar(false);
  }, [setHasSidebar]);

  // On mobile, close the sidebar when the user clicks a nav link inside it.
  const handleNavClick = useCallback((e: React.MouseEvent) => {
    if (window.innerWidth >= 768) return; // desktop: never auto-close
    const target = (e.target as HTMLElement).closest('a, button[data-nav]');
    if (target && target.tagName === 'A') {
      toggle();
    }
  }, [toggle]);

  return (
    <>
      {/* Sidebar container */}
      <aside
        aria-label={t('layout.sidebar.label', 'Navigation du module')}
        className={`fixed md:relative inset-0 md:inset-y-0 md:left-0 bg-background md:border-r border-slate-200 flex flex-col z-[${Z.sidebar}] transition-all duration-200 ease-in-out ${isOpen ? 'translate-x-0 md:w-64' : '-translate-x-full md:translate-x-0 md:w-6'}`}
      >
        {/* Mobile header */}
        <div className="flex items-center justify-between px-4 pb-3 pt-4 overlay-safe md:hidden border-b border-slate-200">
          <span className="font-semibold text-slate-800">Menu</span>
          <button onClick={toggle} aria-label={t('layout.sidebar.close', 'Fermer le menu')} title={t('layout.sidebar.close', 'Fermer le menu')} className="p-1 text-slate-500 hover:text-slate-900">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Desktop: collapse button — absolutely positioned so it doesn't shift content */}
        <button
          onClick={toggle}
          aria-label={t('layout.sidebar.collapse', 'Réduire le menu')}
          title={t('layout.sidebar.collapse', 'Réduire le menu')}
          aria-expanded={isOpen}
          className={`absolute top-3 right-2 hidden ${isOpen ? 'md:flex' : ''} items-center justify-center p-1 rounded-sm text-slate-500 hover:text-slate-600 hover:bg-slate-200 transition-colors z-10`}
        >
          <ChevronLeft size={16} aria-hidden="true" />
        </button>

        {/* Nav content — hidden on desktop when collapsed (rail takes over) */}
        <div
          className={`flex-1 overflow-y-auto overscroll-contain px-2 py-4 flex flex-col gap-6 ${!isOpen ? 'md:hidden' : ''}`}
          onClick={handleNavClick}
        >
          {sidebarContent}
        </div>

        {/* Desktop: collapsed rail — covers the entire aside, click to expand */}
        <button
          onClick={toggle}
          aria-label={t('layout.sidebar.expand', 'Ouvrir le menu')}
          title={t('layout.sidebar.expand', 'Ouvrir le menu')}
          aria-expanded={isOpen}
          className={`absolute inset-0 hidden ${!isOpen ? 'md:flex' : ''} flex-col items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors`}
        >
          <ChevronRight size={18} aria-hidden="true" />
        </button>
      </aside>

      {/* Main Content container */}
      <main className="flex-1 flex flex-col overflow-hidden bg-white relative">
        {mainContent}
      </main>
    </>
  );
}
